import React, { FC, RefObject } from 'react';
import { Dropdown } from 'react-bootstrap';
import { DropdownMenuProps } from 'react-bootstrap/esm/DropdownMenu';
import classNames from 'classnames';

import { createUseThemedStyles } from '@/hooks';
import { boxShadow } from '@/jss';
import { focus } from '@/jss/mixins';
import { DropdownToggleProps } from 'react-bootstrap/esm/DropdownToggle';

const useStyles = createUseThemedStyles((theme) => ({
	dropdownMenu: {
		border: 0,
		...boxShadow,
		minWidth: 240,
		padding: '5px',
		borderRadius: 5,
		overflow: 'hidden',
		'& .dropdown-header': {
			...theme.fonts.xs,
			padding: '8px 24px',
			letterSpacing: '0.083rem',
			textTransform: 'uppercase',
			color: theme.colors.gray600,
			...theme.fonts.openSansSemiBold,
		},
		'& .dropdown-item': {
			borderRadius: 2,
			display: 'flex',
			...theme.fonts.xs,
			padding: '10px 15px',
			alignItems: 'center',
			color: theme.colors.gray900,
			...theme.fonts.openSansRegular,
			'& svg': {
				color: theme.colors.primary,
			},
			'&:hover': {
				textDecoration: 'none',
				backgroundColor: theme.colors.gray100,
			},
			'&:focus': {
				outline: 'none',
			},
			'&:focus-visible': {
				backgroundColor: theme.colors.gray100,
				...focus(theme.colors.primary),
			},
			'&:active': {
				backgroundColor: theme.colors.gray200,
			},
		},
		'& .dropdown-divider': {
			margin: '5px 10px',
			borderColor: theme.colors.gray300,
		},
	},
}));

export const DropdownMenuV2: FC<DropdownMenuProps> = ({ children, className, ...props }) => {
	const classes = useStyles();

	return (
		<Dropdown.Menu className={classNames(classes.dropdownMenu, className)} {...props}>
			{children}
		</Dropdown.Menu>
	);
};

const useDropdownToggleV2Styles = createUseThemedStyles(() => ({
	dropdownButton: {
		appearance: 'none',
		'&:after': {
			display: 'none !important',
		},
	},
}));

export const DropdownToggleV2 = React.forwardRef(
	(
		{ children, className, ...rest }: DropdownToggleProps,
		ref: ((instance: HTMLButtonElement | null) => void) | RefObject<HTMLButtonElement> | null | undefined
	) => {
		const classes = useDropdownToggleV2Styles();
		return (
			<button {...rest} ref={ref} className={classNames(classes.dropdownButton, className)}>
				{children}
			</button>
		);
	}
);
